// src/pages/admin/GameLab.js
//
// A backend bench for every main game and every bonus round, driven by one
// generic set of sample questions, so any of them can be opened and played
// without seeding a topic, without a state or grade, and without touching
// portalLayouts.
//
// WHY THIS IS NOT JUST A LIST OF LINKS
// ------------------------------------
// The games cannot be rendered bare. Three things stand in the way, and each
// one is a reason this file exists rather than a route per game:
//
// 1. ROUTER CONTEXT. CountPop, MatchThreeGame, MemoryMatchGame,
//    MultipleChoice, TalkChoice, DragDropQuiz, TypingQuiz and
//    UniversalMathGame all call useNavigate() and useParams() at the top of
//    the component. Rendered outside a Router they throw immediately; rendered
//    inside the real one they read whatever :subject the admin URL happens to
//    have and their "back" buttons navigate the admin out of the lab. Each
//    preview is mounted in its own MemoryRouter with a synthetic
//    /:subject/:state/:grade/:view/:slug entry, so params resolve and
//    navigation is contained.
//
// 2. TWO DIFFERENT COMPLETION CALLBACKS. BonusRouter passes onComplete to the
//    modern bonus rounds (memoryMatch, match3, countPop, typingQuiz,
//    rhythmTap, puzzleShuffle) and onBonusComplete to the diamondTap family
//    and UniversalMathBonus. A harness that listens for one name silently
//    reports "never completed" for half the catalogue, so the result panel
//    binds both and records which one actually fired.
//
// 3. INCOMPATIBLE QUESTION SHAPES. MultipleChoice reads current.answers,
//    MatchingQuiz reads current.pairs, DragDropQuiz reads current.options with
//    targetText, CountPop reads questions[0] as a settings object, and
//    MatchThree/PuzzleShuffle/RhythmTap ignore questions almost entirely.
//    One array cannot satisfy all of them, so SAMPLES below holds a shape per
//    family and each entry declares which it needs.
//
// The lab deliberately does NOT write scores, progress, or leaderboard
// entries. Games that persist do it through their own onComplete handler in
// UniversalQuizGame, which is not in this tree, so playing here cannot
// pollute a student's record.

import React, { useMemo, useState } from "react";
import { MemoryRouter, Route, Routes } from "react-router-dom";

import MultipleChoice from "../../components/quizzes/MultipleChoice";
import DragDropQuiz from "../../components/quizzes/DragDropQuiz";
import MatchingQuiz from "../../components/quizzes/MatchingQuiz";
import MatchThreeGame from "../../components/quizzes/MatchThreeGame";
import MemoryMatchGame from "../../components/quizzes/MemoryMatchGame";
import CountPop from "../../components/quizzes/CountPop";
import TalkChoice from "../../components/quizzes/TalkChoice";
import TypingQuiz from "../../components/quizzes/TypingQuiz";
import RhythmTapGame from "../../components/quizzes/RhythmTapGame";
import PuzzleShuffle from "../../components/quizzes/PuzzleShuffle";
import UniversalMathGame from "../../components/quizzes/UniversalMathGame";
import BonusRouter from "../../components/quizzes/BonusRouter";

import "../../styles/game-shell.css";

/* ------------------------------------------------------------------ */
/* Sample content                                                      */
/* ------------------------------------------------------------------ */

// Five questions, one shape per family. The content is deliberately dull and
// self-evidently fake: a lab question that reads like real curriculum ends up
// copied into a real topic eventually.
const SAMPLES = {
  // MultipleChoice, TalkChoice, TypingQuiz
  answers: [
    {
      question: "Sample 1 — which answer is marked correct?",
      answers: [
        { text: "Correct answer", correct: true },
        { text: "Wrong answer A", correct: false },
        { text: "Wrong answer B", correct: false },
        { text: "Wrong answer C", correct: false },
      ],
    },
    {
      question: "Sample 2 — pick the right one.",
      answers: [
        { text: "Wrong answer A", correct: false },
        { text: "Correct answer", correct: true },
        { text: "Wrong answer B", correct: false },
        { text: "Wrong answer C", correct: false },
      ],
    },
    {
      question: "Sample 3 — a longer prompt, to check how the card wraps when the question runs past a single line of text.",
      answers: [
        { text: "Wrong answer A", correct: false },
        { text: "Wrong answer B", correct: false },
        { text: "Correct answer", correct: true },
        { text: "Wrong answer C", correct: false },
      ],
    },
    {
      question: "Sample 4 — two answers only.",
      answers: [
        { text: "Correct answer", correct: true },
        { text: "Wrong answer A", correct: false },
      ],
    },
    {
      question: "Sample 5 — last one.",
      answers: [
        { text: "Wrong answer A", correct: false },
        { text: "Wrong answer B", correct: false },
        { text: "Wrong answer C", correct: false },
        { text: "Correct answer", correct: true },
      ],
    },
  ],

  // DragDropQuiz, MemoryMatchGame
  options: [
    {
      question: "Sample 1 — drag each item onto the group it belongs with.",
      options: [
        { text: "Item A1", targetText: "Group A" },
        { text: "Item B1", targetText: "Group B" },
        { text: "Item A2", targetText: "Group A" },
        { text: "Item B2", targetText: "Group B" },
      ],
    },
    {
      question: "Sample 2 — three groups this time.",
      options: [
        { text: "Item A", targetText: "Group A" },
        { text: "Item B", targetText: "Group B" },
        { text: "Item C", targetText: "Group C" },
      ],
    },
    {
      question: "Sample 3 — six items across two groups.",
      options: [
        { text: "Item A1", targetText: "Group A" },
        { text: "Item A2", targetText: "Group A" },
        { text: "Item A3", targetText: "Group A" },
        { text: "Item B1", targetText: "Group B" },
        { text: "Item B2", targetText: "Group B" },
        { text: "Item B3", targetText: "Group B" },
      ],
    },
    {
      question: "Sample 4 — one item per group.",
      options: [
        { text: "Only item A", targetText: "Group A" },
        { text: "Only item B", targetText: "Group B" },
      ],
    },
    {
      question: "Sample 5 — last one.",
      options: [
        { text: "Item A", targetText: "Group A" },
        { text: "Item B", targetText: "Group B" },
        { text: "Item C", targetText: "Group C" },
        { text: "Item D", targetText: "Group A" },
      ],
    },
  ],

  // MatchingQuiz reads current.pairs with {left, right}.
  pairs: [
    {
      prompt: "Sample 1 — match each left item to its partner.",
      pairs: [
        { left: "Left one", right: "Right one" },
        { left: "Left two", right: "Right two" },
        { left: "Left three", right: "Right three" },
      ],
    },
    {
      prompt: "Sample 2 — four pairs.",
      pairs: [
        { left: "Alpha", right: "First" },
        { left: "Beta", right: "Second" },
        { left: "Gamma", right: "Third" },
        { left: "Delta", right: "Fourth" },
      ],
    },
    {
      prompt: "Sample 3 — two pairs only.",
      pairs: [
        { left: "Left one", right: "Right one" },
        { left: "Left two", right: "Right two" },
      ],
    },
    {
      prompt: "Sample 4 — longer labels, to check truncation.",
      pairs: [
        { left: "A left label that runs long", right: "A right label that also runs long" },
        { left: "Short left", right: "Short right" },
      ],
    },
    {
      prompt: "Sample 5 — last one.",
      pairs: [
        { left: "Left A", right: "Right A" },
        { left: "Left B", right: "Right B" },
        { left: "Left C", right: "Right C" },
      ],
    },
  ],

  // CountPop treats questions[0] as a settings object, not a question. Passing
  // it an answers-shaped array yields a silent default board, which reads as
  // "the game is broken" when it is really "the data was the wrong shape".
  countPop: [
    {
      maxNumber: 20,
      target: 7,
      prompt: "Sample — pop exactly 7.",
    },
  ],

  // MatchThree, PuzzleShuffle and RhythmTap generate their own boards. They
  // still receive an array so that a component reading questions[0] for a
  // title or background does not hit undefined.
  generated: [{ prompt: "Sample — this game builds its own board." }],
};

const SAMPLE_LABELS = {
  answers: "5 multiple-choice questions",
  options: "5 drag-and-drop sets",
  pairs: "5 matching sets",
  countPop: "1 settings object (CountPop reads questions[0] as config)",
  generated: "self-generated board",
};

// A neutral placeholder. Several games render placeholderImage as a card face
// or background and show an empty box without one.
const PLACEHOLDER =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="240" height="240">
       <rect width="240" height="240" fill="#e8eef7"/>
       <text x="120" y="128" font-family="system-ui,sans-serif" font-size="18"
             fill="#5a6b85" text-anchor="middle">sample image</text>
     </svg>`
  );

/* ------------------------------------------------------------------ */
/* Catalogue                                                           */
/* ------------------------------------------------------------------ */

// gameType strings are the values UniversalQuizGame's switch accepts after
// normalizeGameType() strips punctuation and applies GAME_TYPE_ALIASES. They
// are written here in the spelling a topic would actually carry, so a working
// entry in this lab is a string that can be pasted into a topic.
const MAIN_GAMES = [
  {
    id: "multipleChoice",
    label: "Multiple Choice",
    gameType: "multipleChoice",
    sample: "answers",
    Component: MultipleChoice,
    note: "Reads current.answers. Crashes on drag-drop data, which is why reconcileGameType exists.",
  },
  {
    id: "dragDrop",
    label: "Drag and Drop",
    gameType: "dragDrop",
    sample: "options",
    Component: DragDropQuiz,
    note: 'Topics files also spell this "dragAndDrop"; the alias map folds both to dragdrop.',
  },
  {
    id: "matching",
    label: "Matching",
    gameType: "matching",
    sample: "pairs",
    Component: MatchingQuiz,
    note: "Reads current.pairs — a different shape from every other game here.",
  },
  {
    id: "match3",
    label: "Match Three",
    gameType: "match3",
    sample: "generated",
    Component: MatchThreeGame,
    note: "Generates its own 6x6 board. Starting-board solvability is a known open issue.",
  },
  {
    id: "memoryMatch",
    label: "Memory Match",
    gameType: "memoryMatch",
    sample: "options",
    Component: MemoryMatchGame,
    note: "Builds card pairs from options[].text and targetText.",
  },
  {
    id: "countPop",
    label: "Count Pop",
    gameType: "countPop",
    sample: "countPop",
    Component: CountPop,
    note: "questions[0] is configuration, not a question. Sound URLs are unverified.",
  },
  {
    id: "talkChoice",
    label: "Talk Choice",
    gameType: "talkChoice",
    sample: "answers",
    Component: TalkChoice,
    note: "Uses speechSynthesis. Known cosmetic score bug on the final question.",
  },
];

// Main games reachable only through a "mode:value" gameType string. These are
// not in the switch — parseMathGameType intercepts them first.
const MATH_GAMES = [
  { id: "mathTimesTable", label: "Times table", mode: "mathTimesTable", value: 5 },
  { id: "mathAddition", label: "Addition", mode: "mathAddition", value: 5 },
  { id: "mathSubtraction", label: "Subtraction", mode: "mathSubtraction", value: 5 },
];

// bonusRoundType strings, as BonusRouter normalises them.
//
// The diamondTap entry is the interesting one: it is not a single game. It
// falls through to bonusRoundMap keyed on SLUG, so which component you get
// depends on the topic, and "default" is BonusDiamonds. The slug field below
// drives that lookup, which is the only way to preview the per-slug variants.
const BONUS_ROUNDS = [
  { id: "diamondTap", label: "Diamond Tap (default)", bonusRoundType: "diamondTap", slug: "__lab__", sample: "answers" },
  { id: "diamondYellow", label: "Diamond Tap — yellow variant", bonusRoundType: "diamondTap", slug: "motion", sample: "answers" },
  { id: "diamondPlane", label: "Side-scroller — plane", bonusRoundType: "diamondTap", slug: "matter", sample: "answers" },
  { id: "memoryMatchBonus", label: "Memory Match", bonusRoundType: "memoryMatch", sample: "options" },
  { id: "match3Bonus", label: "Match Three", bonusRoundType: "match3", sample: "generated" },
  { id: "countPopBonus", label: "Count Pop", bonusRoundType: "countPop", sample: "countPop" },
  { id: "typingQuizBonus", label: "Typing Quiz", bonusRoundType: "typingQuiz", sample: "answers" },
  { id: "rhythmTapBonus", label: "Rhythm Tap", bonusRoundType: "rhythmTap", sample: "generated" },
  { id: "puzzleShuffleBonus", label: "Puzzle Shuffle", bonusRoundType: "puzzleShuffle", sample: "generated" },
  { id: "noneBonus", label: "None (skips straight to results)", bonusRoundType: "none", sample: "generated" },
];

// UniversalMathBonus is reached through "mode:value", same as the math games.
const MATH_BONUS = [
  { id: "bonusTimesTable", label: "Times table", bonusRoundType: "timesTable:5" },
  { id: "bonusCountBy", label: "Count by", bonusRoundType: "countBy:5" },
  { id: "bonusAddition", label: "Addition", bonusRoundType: "addition:5" },
  { id: "bonusSubtraction", label: "Subtraction", bonusRoundType: "subtraction:5" },
];

// Side-scroller themes exist as thin wrappers around BonusSideScroller, but
// only `plane` is reachable from bonusRoundMap today (on the `matter` slug).
// car, rocket and fish have no slug pointing at them, so nothing in the app
// can currently render them. Listed here so that stays visible rather than
// being rediscovered later.
const UNREACHABLE = [
  { label: "Side-scroller — car", detail: "BonusCarRace, no slug in bonusRoundMap" },
  { label: "Side-scroller — rocket", detail: "BonusRocketRace, no slug in bonusRoundMap" },
  { label: "Side-scroller — fish", detail: "BonusFishWorld, no slug in bonusRoundMap" },
];

/* ------------------------------------------------------------------ */
/* Preview frame                                                       */
/* ------------------------------------------------------------------ */

/**
 * Mounts one game inside its own MemoryRouter.
 *
 * `key` on this component is what resets a game — remounting is the only
 * reliable way, since none of them expose a reset and several hold board state
 * in refs that a prop change will not clear.
 */
const PreviewFrame = ({ children }) => (
  <MemoryRouter initialEntries={["/science/lab/3rd/educator/__lab__"]}>
    <Routes>
      <Route path="/:subject/:state/:grade/:view/:slug" element={children} />
      {/* Games navigate on exit. Without a catch-all the MemoryRouter renders
          nothing and the panel looks like it crashed. */}
      <Route
        path="*"
        element={
          <div className="gl-exited">
            The game navigated away. That is the exit path working — press
            Reload to play it again.
          </div>
        }
      />
    </Routes>
  </MemoryRouter>
);

/* ------------------------------------------------------------------ */
/* Main                                                                */
/* ------------------------------------------------------------------ */

const GameLab = () => {
  const [selected, setSelected] = useState({ kind: "main", id: "multipleChoice" });
  const [runId, setRunId] = useState(0);
  const [result, setResult] = useState(null);

  const reload = () => {
    setResult(null);
    setRunId((n) => n + 1);
  };

  const choose = (kind, id) => {
    setSelected({ kind, id });
    setResult(null);
    setRunId((n) => n + 1);
  };

  // Bound to BOTH callback names. Which one fired is recorded, because a game
  // wired to the wrong prop name looks identical to a game that never
  // completes, and that distinction is most of what this lab is for.
  const makeHandlers = () => ({
    onComplete: (payload) => setResult({ via: "onComplete", payload }),
    onBonusComplete: (payload) => setResult({ via: "onBonusComplete", payload }),
  });

  const topicFor = (entry) => ({
    slug: entry.slug || "__lab__",
    title: `Lab — ${entry.label}`,
    description: "Sample content. Not from any state or standard.",
    gameType: entry.gameType || "multipleChoice",
    bonusRoundType: entry.bonusRoundType || "diamondTap",
    placeholderImage: PLACEHOLDER,
    videoUrl: "",
    musicUrl: "",
  });

  const active = useMemo(() => {
    if (selected.kind === "main") {
      return MAIN_GAMES.find((g) => g.id === selected.id) || MAIN_GAMES[0];
    }
    if (selected.kind === "math") return MATH_GAMES.find((g) => g.id === selected.id);
    if (selected.kind === "bonus") return BONUS_ROUNDS.find((g) => g.id === selected.id);
    if (selected.kind === "mathBonus") return MATH_BONUS.find((g) => g.id === selected.id);
    return null;
  }, [selected]);

  const renderActive = () => {
    if (!active) return <p>Nothing selected.</p>;

    const handlers = makeHandlers();
    const questions = SAMPLES[active.sample || "answers"] || SAMPLES.answers;
    const topic = topicFor(active);

    if (selected.kind === "main") {
      const { Component } = active;
      return (
        <Component
          questions={questions}
          topic={topic}
          user={{ uid: "lab", displayName: "Lab" }}
          placeholderImage={PLACEHOLDER}
          onComplete={handlers.onComplete}
        />
      );
    }

    if (selected.kind === "math") {
      return (
        <UniversalMathGame
          mode={active.mode}
          value={active.value}
          questions={SAMPLES.generated}
          topic={topic}
          user={{ uid: "lab", displayName: "Lab" }}
          placeholderImage={PLACEHOLDER}
          onComplete={handlers.onComplete}
        />
      );
    }

    // Both bonus kinds go through BonusRouter rather than importing the bonus
    // components directly. Rendering BonusDiamonds here would test the
    // component while leaving the routing untested, and the routing is where
    // the bugs have been.
    return (
      <BonusRouter
        slug={active.slug || "__lab__"}
        bonusRoundType={active.bonusRoundType}
        questions={SAMPLES[active.sample || "answers"] || SAMPLES.answers}
        placeholderImage={PLACEHOLDER}
        onComplete={(payload) => {
          // BonusRouter passes this single prop through under two different
          // names depending on the branch, so both land here. Reporting which
          // branch was taken is left to the panel below.
          setResult({ via: "BonusRouter onComplete", payload });
        }}
      />
    );
  };

  const Group = ({ title, items, kind, hint }) => (
    <div className="gl-group">
      <h3>{title}</h3>
      {hint && <p className="gl-hint">{hint}</p>}
      <div className="gl-chips">
        {items.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`gl-chip ${
              selected.kind === kind && selected.id === item.id ? "is-on" : ""
            }`}
            onClick={() => choose(kind, item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="gl-page">
      <header className="gl-head">
        <div>
          <h1>Game Lab</h1>
          <p className="gl-sub">
            Every game and bonus round, on identical sample content. Nothing
            here writes scores, progress or leaderboard entries.
          </p>
        </div>
        <button type="button" className="gl-btn" onClick={reload}>
          Reload game
        </button>
      </header>

      <div className="gl-body">
        <aside className="gl-side">
          <Group title="Main games" items={MAIN_GAMES} kind="main" />
          <Group
            title="Math games"
            items={MATH_GAMES}
            kind="math"
            hint='Reached by a gameType of "mode:value", e.g. mathTimesTable:5'
          />
          <Group title="Bonus rounds" items={BONUS_ROUNDS} kind="bonus" />
          <Group
            title="Math bonus rounds"
            items={MATH_BONUS}
            kind="mathBonus"
            hint="Note the different vocabulary: bonus uses timesTable, main uses mathTimesTable."
          />

          <div className="gl-group">
            <h3>Built but unreachable</h3>
            <p className="gl-hint">
              These render fine, but no slug in bonusRoundMap points at them, so
              nothing in the app can currently reach them.
            </p>
            <ul className="gl-dead">
              {UNREACHABLE.map((u) => (
                <li key={u.label}>
                  <strong>{u.label}</strong>
                  <span>{u.detail}</span>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        <main className="gl-main">
          {active && (
            <div className="gl-meta">
              <div>
                <span className="gl-key">
                  {selected.kind === "main" || selected.kind === "math"
                    ? "gameType"
                    : "bonusRoundType"}
                </span>
                <code>
                  {selected.kind === "math"
                    ? `${active.mode}:${active.value}`
                    : active.gameType || active.bonusRoundType}
                </code>
              </div>
              <div>
                <span className="gl-key">sample</span>
                <code>{SAMPLE_LABELS[active.sample] || "n/a"}</code>
              </div>
              {active.slug && active.slug !== "__lab__" && (
                <div>
                  <span className="gl-key">slug</span>
                  <code>{active.slug}</code>
                </div>
              )}
            </div>
          )}

          {active?.note && <p className="gl-note">{active.note}</p>}

          <div className="gl-stage" key={`${selected.kind}-${selected.id}-${runId}`}>
            <PreviewFrame>{renderActive()}</PreviewFrame>
          </div>

          <div className={`gl-result ${result ? "is-on" : ""}`}>
            {result ? (
              <>
                <strong>Completed</strong>
                <span>
                  fired <code>{result.via}</code>
                </span>
                <pre>{JSON.stringify(result.payload, null, 2)}</pre>
              </>
            ) : (
              <span>
                Not completed yet. If a game finishes visually but nothing
                appears here, it is calling a completion prop this harness is
                not bound to — worth fixing, because UniversalQuizGame would
                miss it too.
              </span>
            )}
          </div>
        </main>
      </div>

      <style>{`
        .gl-page { padding: 24px; max-width: 1400px; margin: 0 auto; }
        .gl-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; margin-bottom: 20px; }
        .gl-head h1 { margin: 0 0 4px; font-size: 26px; }
        .gl-sub { margin: 0; color: #5a6b85; font-size: 14px; max-width: 60ch; }
        .gl-btn { border: 1px solid #c9d4e5; background: #fff; border-radius: 8px; padding: 9px 16px; cursor: pointer; font-size: 14px; }
        .gl-btn:hover { background: #f2f6fc; }
        .gl-body { display: grid; grid-template-columns: 280px 1fr; gap: 24px; align-items: start; }
        .gl-side { display: flex; flex-direction: column; gap: 22px; }
        .gl-group h3 { margin: 0 0 6px; font-size: 13px; text-transform: uppercase; letter-spacing: .05em; color: #5a6b85; }
        .gl-hint { margin: 0 0 8px; font-size: 12px; color: #7b8aa1; line-height: 1.45; }
        .gl-chips { display: flex; flex-wrap: wrap; gap: 6px; }
        .gl-chip { border: 1px solid #d7dfec; background: #fff; border-radius: 999px; padding: 6px 12px; font-size: 13px; cursor: pointer; }
        .gl-chip:hover { background: #f2f6fc; }
        .gl-chip.is-on { background: #1f4e9c; border-color: #1f4e9c; color: #fff; }
        .gl-dead { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 6px; }
        .gl-dead li { font-size: 12px; display: flex; flex-direction: column; color: #7b8aa1; }
        .gl-dead strong { color: #3d4a5c; font-weight: 600; }
        .gl-meta { display: flex; flex-wrap: wrap; gap: 18px; margin-bottom: 10px; font-size: 13px; }
        .gl-key { color: #7b8aa1; margin-right: 6px; }
        .gl-meta code, .gl-result code { background: #eef3fa; padding: 2px 7px; border-radius: 5px; font-size: 12px; }
        .gl-note { margin: 0 0 12px; font-size: 13px; color: #8a6d3b; background: #fdf6e3; border: 1px solid #f0e2bd; border-radius: 8px; padding: 9px 12px; }
        .gl-stage { border: 1px solid #dbe3ee; border-radius: 12px; min-height: 520px; overflow: hidden; background: #fff; position: relative; }
        .gl-exited { padding: 40px; text-align: center; color: #7b8aa1; font-size: 14px; }
        .gl-result { margin-top: 14px; border: 1px dashed #d7dfec; border-radius: 10px; padding: 12px 14px; font-size: 13px; color: #7b8aa1; }
        .gl-result.is-on { border-style: solid; border-color: #b6d7b9; background: #f2faf3; color: #2f5d38; }
        .gl-result strong { margin-right: 10px; }
        .gl-result pre { margin: 8px 0 0; font-size: 12px; white-space: pre-wrap; word-break: break-word; }
        @media (max-width: 900px) {
          .gl-body { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
};

export default GameLab;
